/**
 * useOneTimeHint — Vincent B598.c (Eric 2026-07-29)
 *
 * Hook réutilisable pour afficher un indicateur textuel discret ("hint")
 * une seule fois par session, avec auto-timeout et dismiss manuel.
 *
 * Usage :
 *   const { show, dismiss } = useOneTimeHint("kn-my-feature-hint", 8000);
 *   ...
 *   {show && <span onClick={dismiss}>Cliquez ici pour X</span>}
 *
 * @param {string} storageKey — clé unique dans sessionStorage
 * @param {number} timeoutMs — durée d'affichage avant auto-dismiss (défaut 8000)
 * @param {boolean} enabled — condition externe pour activer le hint (défaut true)
 * @returns {{ show: boolean, dismiss: () => void }}
 */
import { useEffect, useState, useCallback } from "react";

export function useOneTimeHint(storageKey, timeoutMs = 8000, enabled = true) {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    try {
      if (sessionStorage.getItem(storageKey) === "1") return;
      setShow(true);
      const t = setTimeout(() => {
        setShow(false);
        try { sessionStorage.setItem(storageKey, "1"); } catch (_) { /* private mode */ }
      }, timeoutMs);
      return () => clearTimeout(t);
    } catch (_) { /* private mode → hint not shown, safe fallback */ }
  }, [storageKey, timeoutMs, enabled]);

  const dismiss = useCallback(() => {
    setShow(false);
    try { sessionStorage.setItem(storageKey, "1"); } catch (_) { /* ignore */ }
  }, [storageKey]);

  return { show, dismiss };
}
